"use client";

import { setClientToken } from "@onsand/api";
import { auth } from "@onsand/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { LockKeyhole, Mail } from "@/icons/lucide";

export default function LoginPage() {
	const router = useRouter();
	const t = useTranslations("login");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError(null);
		try {
			const credential = await signInWithEmailAndPassword(auth, email, password);
			setClientToken(await credential.user.getIdToken());
			router.push("/");
		} catch {
			setError(t("loginFailed"));
		} finally {
			setLoading(false);
		}
	}

	return (
		<Card className="rounded-2xl">
			<CardHeader className="gap-2">
				<CardTitle className="font-bold font-heading text-2xl tracking-tight">
					{t("signIn")}
				</CardTitle>
				<CardDescription>{t("signInDescription")}</CardDescription>
			</CardHeader>

			<CardContent>
				<form onSubmit={handleSubmit} className="grid gap-3">
					<Field className="gap-0">
						<div className="relative w-full">
							<Mail
								strokeWidth={2}
								className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
							/>
							<Input
								type="email"
								placeholder={t("email")}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								className="h-11 w-full rounded-md pl-10"
							/>
						</div>
					</Field>
					<Field className="gap-0">
						<div className="relative w-full">
							<LockKeyhole
								strokeWidth={2}
								className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
							/>
							<Input
								type="password"
								placeholder={t("password")}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								className="h-11 w-full rounded-md pl-10"
							/>
						</div>
					</Field>
					{error && <p className="font-medium text-destructive text-sm">{error}</p>}
					<Button type="submit" disabled={loading} className="mt-1 h-11 rounded-md font-semibold">
						{loading ? t("signingIn") : t("signIn")}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
}
